import React, { useContext, useRef } from 'react';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import _merge from 'lodash/merge';
import getContext from 'common/get-context';
import KEY_CODES from 'common/KeyCodes';

import useCombinedRefs from 'common/useCombinedRefs';
import IconButton, { IconButtonProps } from 'components/IconButton';
import { RippleEffectProps } from '../RippleEffect';

import { ChipContext } from './Chip';

/* The remove button should be on top of the borders rendered by Chip */
const ICON_BUTTON_STYLE = {
  position: 'relative' as const,
  zIndex: 1
};

const DEFAULT_STYLE = RTL => RTL ? ({
  marginRight: `-${Tokens.SPACING.SPACING_4}`
}) : ({
  marginLeft: `-${Tokens.SPACING.SPACING_4}`
});

const GET_COLOR = (theme, { focused, hovered }) =>
  (focused && hovered) ? theme.hoveredFocused.icon
    : focused ? theme.focused.icon
      : hovered ? theme.hovered.icon : theme.icon;

export interface ChipIconButtonProps extends IconButtonProps {
  /** URL of Icon (alternatively you can also use the 'children' prop to pass in an SVG.) */
  src?: string;
  /** To override the content of the IconButton. */
  children: React.ReactNode;
  /** To override the style of the outermost element. */
  rippleStyle?: React.CSSProperties;
  /** To config the RippleEffect component. */
  rippleProps?: Partial<RippleEffectProps>;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** To override the theme. */
  theme?: any;
}
/** All props will be provided to `<IconButton />` component, the size of the `ChipIcon` is read from `ChipContext`. */
const ChipIconButton = React.forwardRef<HTMLElement, ChipIconButtonProps>((props: ChipIconButtonProps, ref) => getContext(({ RTL, theme }) => {
  const {
    children,
    rippleStyle,
    onFocus,
    onKeyDown,
    rippleProps,
    theme: themeProp,
    ...otherProps
  } = props;
  const {
    focused,
    hovered,
    selected,
    error,
    size,
    chipRef,
  } = useContext(ChipContext);
  const resolvedTheme = _merge({}, theme.CHIP, themeProp);
  const resolvedIconButtonTheme = { ...theme.ICON_BUTTON.FLAT };
  const resolvedColor =
    selected ? GET_COLOR(resolvedTheme.selected, { focused, hovered })
      : error ? GET_COLOR(resolvedTheme.error, { focused, hovered })
        : GET_COLOR(resolvedTheme.default, { focused, hovered });

  resolvedIconButtonTheme.content = resolvedColor;
  resolvedIconButtonTheme.hovered.content = resolvedColor;
  resolvedIconButtonTheme.focused.content = resolvedColor;
  const containerRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, containerRef);
  const handleOnFocus = (event) => {
    event.stopPropagation();
    onFocus && onFocus(event);
  };

  const handleKeyDown = (event) => {
    if (chipRef && event.key === KEY_CODES.ARROW_UP) {
      chipRef && chipRef.current && chipRef.current.focus();
      event.stopPropagation();
    }
    onKeyDown && onKeyDown(event);
  };

  return (
    <IconButton
      style={ICON_BUTTON_STYLE}
      theme={resolvedIconButtonTheme}
      ref={combinedRef}
      size={size}
      tabIndex={-1}
      rippleStyle={_merge({}, DEFAULT_STYLE(RTL), rippleStyle)}
      onKeyDown={handleKeyDown}
      onFocus={handleOnFocus}
      rippleProps={{
        onMouseDown: (event) => { event.stopPropagation(); }, // To prevent ripple effect on the Chip when IconButton is pressed.
        onKeyDown: (event) => { event.stopPropagation(); }, // To prevent ripple effect on the Chip when IconButton is pressed.
        onMouseUp: (event) => { event.stopPropagation(); }, // To prevent ripple effect on the Chip when IconButton is pressed.
        ...rippleProps
      }}
      {...otherProps}
    >{children}
    </IconButton>
  );
}));

ChipIconButton.displayName = 'ChipIconButton';

ChipIconButton.defaultProps = {
  src: undefined,
  rippleStyle: {},
  rippleProps: {},
  onFocus: undefined,
  onKeyDown: undefined,
  theme: {},
};

export default ChipIconButton;
